/* eslint-disable react-hooks/rules-of-hooks */
import axios from 'axios';
import {observer} from 'mobx-react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {Controller} from 'react-hook-form';
import {useSearchParams} from 'react-router-dom';
import {Button} from '@ui/atoms/button';
import {ArrowHeadDownIcon, CancelIcon, MinusIcon} from '@ui/atoms/icons';
import {Helper} from '@ui/atoms/helper';
import {Label} from '@ui/atoms/label';
import {Headline4} from '@ui/atoms/typography';
import {DateRangeInput} from '@ui/molecules/date-range-input';
import {GoalTypeField} from '@ui/molecules/field/goal-type-field';
import {TextField} from '@ui/molecules/field/textfield';
import {GoalsSelectField} from '@ui/molecules/select/goal-select-field/goal-select-field';
import {MultiOptionsSelectFieldGroup} from '@ui/molecules/select/multi-options';
import {SelectOptionItem} from '@ui/molecules/select/multi-options/types';
import {OptionSelectField} from '@ui/molecules/select/option';
import {SelectField} from '@ui/molecules/select/selectfield';
import {UserSelectField} from '@ui/molecules/select/user';
import {ItemLoader} from '@ui/organisms/item-loader';
import {KeyResults} from '@ui/organisms/keyresults/keyresult';
import {MeasurementType, GoalKind} from '@ui/interface';
import {BaseURL} from '@configs/request';
import {authStore} from '@store/stores/auth-store';
import {useStoreContext} from '@store/store-context';
import {cfv} from '@utils/framework';
import {metricDirection} from '@utils/proportions';
import AddDescription from './add-description';
import {
  Divider,
  CreateGoalSubmitButton,
  EditCloseButton,
  EditHeader,
  EditTitle,
  FormActions,
  FormRow,
  FormSection,
  FormStack,
  InfoPanel,
  KpiCurrencySelect,
  KpiMetricFields,
  KpiMetricSelectorRow,
  KpiSegmentedControlButton,
  KpiValueInput,
  KpiValueInputShell,
  KpiValuePrefix,
  KpiValueRow,
  OptionalAction,
  OptionalPill,
  ParentGoalCollapse,
  ParentGoalContribution,
  ParentGoalDescription,
  ParentGoalHeader,
  ParentGoalPanel,
  ParentGoalSection,
  RemoveAlignmentButton,
  ResultsSection,
  SegmentedControl,
  SegmentedControlButton,
} from './create-goal.styles';
import {useCreateGoalHook} from './create-goal-hooks';
import {CreateGoalFormData} from './create-goal-interface';
import {useGoalPeriods} from './goal-periods';
import {useParentGoalOptions} from './use-parent-goals';

const metricOptions = [
  {label: 'Number', value: MeasurementType.NUMERIC},
  {label: 'Percentage', value: MeasurementType.PERCENTAGE},
  {label: 'Currency', value: MeasurementType.CURRENCY},
];

const currencyOptions = [
  {label: 'USD $', value: '$'},
  {label: 'EUR €', value: '€'},
  {label: 'GBP £', value: '£'},
  {label: 'NGN ₦', value: '₦'},
];

const updateOptions = [
  {label: 'Latest value', value: 'latest'},
  {label: 'Total', value: 'sum'},
  {label: 'Average', value: 'average'},
];

const alignmentContributionOptions = [
  {
    label: 'Blend the parent’s outcome with its aligned goals',
    value: 'all',
  },
  {
    label: 'Use only the parent’s own outcome',
    value: 'key results',
  },
  {
    label: 'Use only its assigned goals',
    value: 'children',
  },
];

const getGoalAlignmentId = (goalAlignment: any) =>
  typeof goalAlignment === 'string'
    ? goalAlignment
    : goalAlignment?.value || goalAlignment?.id || '';

export const CreateGoalForm = observer(
  ({
    onSubmit,
    loading: isSubmitting,
    editheaderShow,
    handleCloseDrawer,
  }: {
    onSubmit: (data: CreateGoalFormData) => Promise<void>;
    editheaderShow?: boolean;
    loading?: boolean;
    handleCloseDrawer?: any;
  }) => {
    const {
      handleSubmit,
      submitForm,
      register,
      errors,
      updateKeyResult,
      control,
      handleFormValueChange,
      watch,
      isFetching,
      editable,
      users,
      submitEditedForm,
      isGoalKpi,
      handleSelectGroup,
      loader,
      setValue,
      isEditingDisabled,
      clearErrors,
    } = useCreateGoalHook(onSubmit);

    const {
      authStore: {auth},
      groupStore: {groups},
      groupTypeStore: {groupType},
      goalsState: {isEditing},
    } = useStoreContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const duplicateGoal = searchParams.get('duplicate_goal');
    const exploreTemplate = searchParams.get('explore-templates');
    const formValues = watch();
    const [members, setMembers] = useState(users);
    const [selectedGoalType, setSelectedGoalType] = useState<GoalKind>(
      GoalKind.INDIVIDUAL,
    );
    const [selectedPeriod, setSelectedPeriod] = useState<string>();
    const [showAlignment, setShowAlignment] = useState(false);
    const [selectedParentGoalId, setSelectedParentGoalId] = useState(() =>
      getGoalAlignmentId(formValues.goalAlignment),
    );
    const [selectedKpiMetric, setSelectedKpiMetric] = useState<MeasurementType>(
      MeasurementType.NUMERIC,
    );
    const [selectedKpiUpdate, setSelectedKpiUpdate] = useState('latest');
    const {goalPeriods, currentPeriodValue} = useGoalPeriods();
    const canAlign = Boolean(auth?.user?.workspace?.settings?.goalAlignment);
    const canCreateKpi = Boolean(auth?.user?.workspace?.config?.allowKpi);
    const {
      options: parentGoalOptions,
      isLoading: parentGoalsLoading,
    } = useParentGoalOptions(canAlign && showAlignment && !isGoalKpi);
    const formatOptionsTypes = useMemo(
      () =>
        groupType?.map((type) => ({
          ...type,
          data: groups
            .filter((group) => group.groupType === type.id)
            .map((group) => ({label: group.name, value: group.id})),
        })) || [],
      [groupType, groups],
    );

    useEffect(() => {
      axios
        .get(`${BaseURL}/users?paginate=false`, {
          headers: {
            Authorization: `Bearer ${authStore.auth?.tokens.access.token}`,
          },
        })
        .then((response) => {
          setMembers(
            response.data
              ?.filter((person: any) => person.status.includes('on'))
              .map((member: any) => ({
                label: {
                  avatar: {
                    name: member.firstName
                      ? `${member.firstName} ${member.lastName}`
                      : `${member.email} (pending invitation)`,
                    src: member.avatar?.url || '',
                  },
                  id: member.id,
                  name: member.firstName
                    ? `${member.firstName} ${member.lastName}`
                    : `${member.email} (pending invitation)`,
                },
                value: member.id,
              })) || [],
          );
        });
    }, []);

    useEffect(() => {
      if (!isEditing && !duplicateGoal && !formValues.goalType) {
        setValue('goalType', GoalKind.INDIVIDUAL);
      }
    }, [duplicateGoal, formValues.goalType, isEditing, setValue]);

    useEffect(() => {
      if (!isEditing && isGoalKpi) {
        setSelectedKpiMetric(MeasurementType.NUMERIC);
        setSelectedKpiUpdate('latest');
        setValue('measurement.unit', MeasurementType.NUMERIC);
        setValue('measurement.preference', 'latest');
      }
    }, [isEditing, isGoalKpi, setValue]);

    useEffect(() => {
      if (
        (!selectedPeriod && !currentPeriodValue) ||
        isEditing ||
        duplicateGoal
      ) {
        return;
      }
      const period = goalPeriods.find(
        (option) => option.value === (selectedPeriod || currentPeriodValue),
      );
      if (period?.starts && period?.ends) {
        setSelectedPeriod(period.value);
        setValue('date', {starts: period.starts, ends: period.ends});
      }
    }, [
      currentPeriodValue,
      duplicateGoal,
      goalPeriods,
      isEditing,
      selectedPeriod,
      setValue,
    ]);

    useEffect(() => {
      if (!editable?.goalType) return;
      const goalType =
        editable.goalType.toLowerCase() === 'self-development'
          ? GoalKind.INDIVIDUAL
          : (editable.goalType.toUpperCase() as GoalKind);
      setSelectedGoalType(goalType);
      if (editable.isKpi) {
        setSelectedKpiMetric(
          (editable.measurement?.unit as MeasurementType) ||
            MeasurementType.NUMERIC,
        );
        setSelectedKpiUpdate(editable.measurement?.preference || 'latest');
      }
      if (editable.goalAlignment) {
        setShowAlignment(true);
        setSelectedParentGoalId(getGoalAlignmentId(editable.goalAlignment));
      }
      if (editable.date?.starts && editable.date?.ends) {
        const matchingPeriod = goalPeriods.find(
          (period) =>
            period.starts === editable.date.starts &&
            period.ends === editable.date.ends,
        );
        setSelectedPeriod(matchingPeriod?.value || 'custom');
      }
    }, [editable, goalPeriods]);

    const changeCreationType = useCallback(
      (kpi: boolean) => {
        if (isEditing || (kpi && !canCreateKpi)) return;
        setValue(
          'measurement',
          kpi
            ? {unit: MeasurementType.NUMERIC, preference: 'latest'}
            : {unit: MeasurementType.BINARY},
        );
        setValue('range', {});
        if (kpi) {
          setSelectedKpiMetric(MeasurementType.NUMERIC);
          setSelectedKpiUpdate('latest');
          setValue('keyResults', []);
          setValue('goalAlignment', '');
          setValue('alignmentContribution', '');
          setSelectedParentGoalId('');
          setShowAlignment(false);
        } else if (!formValues.keyResults?.length) {
          setValue('keyResults', [
            {
              id: '',
              measurement: {unit: MeasurementType.BINARY, symbol: ''},
              name: '',
              range: {},
              user: auth.user.id,
              showSource: false,
              saveSource: false,
              source: null,
            },
          ]);
        }
        clearErrors();
        const next = new URLSearchParams(searchParams);
        if (kpi) next.set('kpi', 'true');
        else next.delete('kpi');
        setSearchParams(next, {replace: true});
      },
      [
        auth.user.id,
        canCreateKpi,
        clearErrors,
        formValues.keyResults,
        isEditing,
        searchParams,
        setSearchParams,
        setValue,
      ],
    );

    const onPeriodChange = (
      option: {value: string},
      onChange: (value: {starts: string; ends: string}) => void,
    ) => {
      setSelectedPeriod(option.value);
      const period = goalPeriods.find((item) => item.value === option.value);
      if (period?.starts && period?.ends) {
        onChange({starts: period.starts, ends: period.ends});
      } else {
        setValue('date', {starts: '', ends: ''});
        clearErrors('date');
      }
    };

    const handleInvalidSubmit = useCallback(() => {
      window.requestAnimationFrame(() => {
        const firstError = Array.from(
          document.querySelectorAll<HTMLElement>('[aria-label="helper"]'),
        ).find((helper) => Boolean(helper.textContent?.trim()));

        if (!firstError) return;

        const focusTarget = firstError.parentElement?.querySelector<HTMLElement>(
          'input:not([type="hidden"]):not(:disabled), textarea:not(:disabled), button:not(:disabled)',
        );
        focusTarget?.focus({preventScroll: true});
        (focusTarget || firstError).scrollIntoView?.({
          behavior: 'smooth',
          block: 'center',
        });
      });
    }, []);
    const handleGoalSubmit = handleSubmit(
      isEditing ? submitEditedForm : submitForm,
      handleInvalidSubmit,
    );

    if (isFetching) return <ItemLoader />;

    return (
      <form method={isEditing ? 'patch' : 'post'} onSubmit={handleGoalSubmit}>
        <FormStack $sidePanel={Boolean(editheaderShow)}>
          {isEditing && editheaderShow && (
            <EditHeader>
              <EditTitle>
                Edit {isGoalKpi ? 'KPI' : cfv().g_oal.toLowerCase()}
              </EditTitle>
              <EditCloseButton type="button" onClick={handleCloseDrawer}>
                <CancelIcon />
                Close
              </EditCloseButton>
            </EditHeader>
          )}

          {canCreateKpi && (
            <FormSection>
              <Label>Type</Label>
              <SegmentedControl role="group" aria-label="Goal type">
                <SegmentedControlButton
                  type="button"
                  active={!isGoalKpi}
                  disabled={isEditing}
                  onClick={() => changeCreationType(false)}>
                  Goal
                </SegmentedControlButton>
                <SegmentedControlButton
                  type="button"
                  active={isGoalKpi}
                  disabled={isEditing}
                  onClick={() => changeCreationType(true)}>
                  KPI
                </SegmentedControlButton>
              </SegmentedControl>
            </FormSection>
          )}

          <Controller
            control={control}
            name="goalType"
            defaultValue={GoalKind.INDIVIDUAL}
            render={({value, onChange}) => (
              <GoalTypeField
                label="Goal for"
                name="goalType"
                compact={Boolean(editheaderShow)}
                value={value || selectedGoalType}
                setValue={(goalType) => {
                  setSelectedGoalType(goalType);
                  onChange(goalType);
                  if (goalType !== GoalKind.GROUP) handleSelectGroup([]);
                }}
                individualLabel="Personal"
                noMargin
              />
            )}
          />

          {selectedGoalType === GoalKind.GROUP && (
            <FormSection>
              <Label>Select groups</Label>
              <Controller
                control={control}
                name="group"
                render={({value}) => (
                  <MultiOptionsSelectFieldGroup
                    control={control}
                    placeholder="Select groups"
                    searchPlaceholder="Search group"
                    groupDisplayLength={3}
                    disabled={!!exploreTemplate}
                    helper={errors.group?.message || ''}
                    state={errors.group?.message ? 'error' : 'default'}
                    defaultValue={groups
                      .filter((group) =>
                        value?.some((selected: string | {id: string}) =>
                          typeof selected === 'string'
                            ? selected === group.id
                            : selected.id === group.id,
                        ),
                      )
                      .map((group) => ({label: group.name, value: group.id}))}
                    onChange={(selection: SelectOptionItem[]) =>
                      handleSelectGroup(
                        selection.map((group) => String(group.value)),
                      )
                    }
                    options={formatOptionsTypes}
                    showSelectedOptionsInMenu
                    showSelectionFooter
                    showSelectionAction={false}
                    flatGroupPicker
                    showSelectionCancelIcon={false}
                    compact={Boolean(editheaderShow)}
                    feature="invite"
                  />
                )}
              />
            </FormSection>
          )}

          <FormStack>
            <FormSection>
              <Label>Title</Label>
              <TextField
                placeholder={
                  isGoalKpi
                    ? 'e.g Monthly active users'
                    : 'e.g Increase global numbers'
                }
                type="text"
                ref={register}
                inputStyle={{
                  borderRadius: '10px',
                  ...(editheaderShow
                    ? {
                        height: '44px',
                        padding: '5px 12px',
                        fontSize: '16px',
                        lineHeight: '24px',
                      }
                    : {}),
                }}
                readOnly={!!exploreTemplate}
                helper={errors.name?.message || ''}
                state={errors.name ? 'error' : 'default'}
                name="name"
                margin
              />
            </FormSection>
            <Controller
              control={control}
              name="meta"
              render={({value}) => (
                <AddDescription
                  value={value}
                  isGoalKpi={false}
                  onChange={(data: {tags: string[]; description: string}) =>
                    handleFormValueChange({...formValues.meta, ...data}, 'meta')
                  }
                />
              )}
            />
          </FormStack>

          <FormRow>
            <FormSection>
              <Controller
                control={control}
                name="date"
                render={({onChange}) => (
                  <OptionSelectField
                    label="Period"
                    options={goalPeriods}
                    value={selectedPeriod}
                    placeholder="Select period"
                    excludeSearchBox
                    optionsSelectType="period"
                    customOptionName="Custom date range"
                    compact={Boolean(editheaderShow)}
                    onChange={(option: any) => onPeriodChange(option, onChange)}
                    margin
                  />
                )}
              />
            </FormSection>
            <FormSection>
              <Controller
                control={control}
                name="manager"
                defaultValue={editable ? editable.manager : auth.user.id}
                render={({onBlur, value}) => (
                  <UserSelectField
                    label="Owner"
                    onBlur={onBlur}
                    name="manager"
                    value={value}
                    disabled={!!exploreTemplate}
                    defaultValue={value}
                    helper={errors.manager?.message || ''}
                    state={errors.manager ? 'error' : 'default'}
                    onChange={(data: {value: string}) =>
                      handleFormValueChange(data.value, 'manager')
                    }
                    placeholder="Select owner"
                    searchPlaceholder="Search"
                    compact={Boolean(editheaderShow)}
                    options={members}
                    margin
                  />
                )}
              />
            </FormSection>
          </FormRow>

          {selectedPeriod === 'custom' && (
            <FormSection>
              <Label>Custom date range</Label>
              <DateRangeInput
                name="date"
                value={formValues.date}
                dateAlign="left"
                placeholder="Select dates"
                helper={errors.date?.ends?.message || ''}
                state={errors.date ? 'error' : 'default'}
                admin
                compact={Boolean(editheaderShow)}
                setValue={(date: {starts: string; ends: string}) =>
                  handleFormValueChange(date, 'date')
                }
              />
            </FormSection>
          )}

          {!isGoalKpi &&
            canAlign &&
            selectedGoalType !== GoalKind.COMPANY &&
            !showAlignment && (
              <OptionalAction
                type="button"
                onClick={() => setShowAlignment(true)}>
                + Align to a parent goal <OptionalPill>Optional</OptionalPill>
              </OptionalAction>
            )}

          {!isGoalKpi &&
            canAlign &&
            selectedGoalType !== GoalKind.COMPANY &&
            showAlignment && (
              <ParentGoalSection>
                <ParentGoalHeader>
                  <ParentGoalCollapse
                    type="button"
                    disabled={Boolean(selectedParentGoalId)}
                    title={
                      selectedParentGoalId
                        ? 'Remove the alignment before closing this section'
                        : undefined
                    }
                    onClick={() => {
                      setShowAlignment(false);
                      setSelectedParentGoalId('');
                      handleFormValueChange('', 'goalAlignment');
                      handleFormValueChange('', 'alignmentContribution');
                    }}>
                    <MinusIcon />
                    Align to a parent goal
                  </ParentGoalCollapse>
                  <OptionalPill>Optional</OptionalPill>
                </ParentGoalHeader>
                <ParentGoalPanel>
                  <ParentGoalDescription>
                    Link this goal under a parent so its progress can roll up
                    automatically. Goals left unaligned show up in the Unaligned
                    tab on the Goals list, where you can align them later.
                  </ParentGoalDescription>
                  <Controller
                    control={control}
                    name="goalAlignment"
                    render={({value, onChange}) => (
                      <GoalsSelectField
                        key={selectedParentGoalId || 'unselected-parent-goal'}
                        options={parentGoalOptions}
                        useDropdownIcon
                        compact={Boolean(editheaderShow)}
                        defaultValue={getGoalAlignmentId(value)}
                        placeholder={
                          parentGoalsLoading
                            ? 'Loading goals…'
                            : 'Search parent goal'
                        }
                        disabled={parentGoalsLoading}
                        margin
                        onChange={(goal: {value: string; label: string}) => {
                          if (goal.value !== getGoalAlignmentId(value)) {
                            handleFormValueChange('', 'alignmentContribution');
                          }
                          setSelectedParentGoalId(goal.value);
                          onChange(goal);
                        }}
                      />
                    )}
                  />
                  {selectedParentGoalId && (
                    <>
                      <ParentGoalContribution>
                        <Label>
                          How does your parent progress count this goal?
                        </Label>
                        <Controller
                          control={control}
                          name="alignmentContribution"
                          render={({value}) => (
                            <SelectField
                              key={selectedParentGoalId}
                              options={alignmentContributionOptions}
                              defaultValue={value}
                              excludeSearchBox
                              reportingStatus={Boolean(editheaderShow)}
                              borderRadius="10px"
                              placeholder="Select option"
                              helper={
                                errors.alignmentContribution?.message || ''
                              }
                              state={
                                errors.alignmentContribution
                                  ? 'error'
                                  : 'default'
                              }
                              margin
                              onChange={(option: {value: string}) =>
                                handleFormValueChange(
                                  option.value,
                                  'alignmentContribution',
                                )
                              }
                            />
                          )}
                        />
                      </ParentGoalContribution>
                      <RemoveAlignmentButton
                        type="button"
                        onClick={() => {
                          setSelectedParentGoalId('');
                          handleFormValueChange('', 'goalAlignment');
                          handleFormValueChange('', 'alignmentContribution');
                        }}>
                        <MinusIcon />
                        Remove alignment
                      </RemoveAlignmentButton>
                    </>
                  )}
                </ParentGoalPanel>
              </ParentGoalSection>
            )}

          {isGoalKpi ? (
            <KpiMetricFields>
              <Controller
                control={control}
                name="measurement.unit"
                defaultValue={MeasurementType.NUMERIC}
                render={({value}) => (
                  <input
                    type="hidden"
                    value={value || MeasurementType.NUMERIC}
                    readOnly
                  />
                )}
              />
              <Controller
                control={control}
                name="measurement.preference"
                defaultValue="latest"
                render={({value}) => (
                  <input type="hidden" value={value || 'latest'} readOnly />
                )}
              />
              <Divider />
              <FormSection>
                <Headline4 style={{margin: 0}}>Metric</Headline4>
                <KpiMetricSelectorRow>
                  <SegmentedControl role="group" aria-label="KPI metric">
                    {metricOptions.map((option) => (
                      <KpiSegmentedControlButton
                        key={option.value}
                        type="button"
                        active={selectedKpiMetric === option.value}
                        aria-pressed={selectedKpiMetric === option.value}
                        onClick={() => {
                          setSelectedKpiMetric(option.value);
                          handleFormValueChange(
                            option.value,
                            'measurement.unit',
                          );
                          handleFormValueChange(
                            option.value === MeasurementType.CURRENCY
                              ? formValues.measurement?.symbol || '$'
                              : '',
                            'measurement.symbol',
                          );
                        }}>
                        {option.label}
                      </KpiSegmentedControlButton>
                    ))}
                  </SegmentedControl>
                  {selectedKpiMetric === MeasurementType.CURRENCY && (
                    <Controller
                      control={control}
                      name="measurement.symbol"
                      render={({value}) => (
                        <KpiCurrencySelect>
                          <select
                            value={value || '$'}
                            aria-label="Currency"
                            onChange={(event) =>
                              handleFormValueChange(
                                event.target.value,
                                'measurement.symbol',
                              )
                            }>
                            {currencyOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <ArrowHeadDownIcon />
                        </KpiCurrencySelect>
                      )}
                    />
                  )}
                </KpiMetricSelectorRow>
                <Helper
                  state={errors.measurement?.unit ? 'error' : 'default'}
                  children={errors.measurement?.unit?.message || ''}
                />
              </FormSection>

              <KpiValueRow>
                <FormSection>
                  <Controller
                    control={control}
                    name="range.start"
                    render={({value}) => (
                      <KpiValueInputShell
                        hasError={Boolean(errors.range?.start)}>
                        {selectedKpiMetric !== MeasurementType.CURRENCY && (
                          <KpiValuePrefix aria-hidden="true">
                            {selectedKpiMetric === MeasurementType.PERCENTAGE
                              ? '%'
                              : '#'}
                          </KpiValuePrefix>
                        )}
                        <KpiValueInput
                          type="number"
                          value={value ?? ''}
                          placeholder="Start value"
                          aria-label="Start value"
                          readOnly={isEditing && !duplicateGoal}
                          onChange={(event) =>
                            handleFormValueChange(
                              event.target.value === ''
                                ? undefined
                                : Number(event.target.value),
                              'range.start',
                            )
                          }
                        />
                      </KpiValueInputShell>
                    )}
                  />
                  <Helper
                    state={errors.range?.start ? 'error' : 'default'}
                    children={errors.range?.start?.message || ''}
                  />
                </FormSection>
                <FormSection>
                  <Controller
                    control={control}
                    name="range.target"
                    render={({value}) => (
                      <KpiValueInputShell
                        hasError={Boolean(errors.range?.target)}>
                        {selectedKpiMetric !== MeasurementType.CURRENCY && (
                          <KpiValuePrefix aria-hidden="true">
                            {selectedKpiMetric === MeasurementType.PERCENTAGE
                              ? '%'
                              : '#'}
                          </KpiValuePrefix>
                        )}
                        <KpiValueInput
                          type="number"
                          value={value ?? ''}
                          placeholder="Target value"
                          aria-label="Target value"
                          onChange={(event) =>
                            handleFormValueChange(
                              event.target.value === ''
                                ? undefined
                                : Number(event.target.value),
                              'range.target',
                            )
                          }
                        />
                      </KpiValueInputShell>
                    )}
                  />
                  <Helper
                    state={errors.range?.target ? 'error' : 'default'}
                    children={errors.range?.target?.message || ''}
                  />
                </FormSection>
              </KpiValueRow>

              {formValues.range?.start !== undefined &&
                formValues.range?.target !== undefined &&
                Number.isFinite(Number(formValues.range?.start)) &&
                Number.isFinite(Number(formValues.range?.target)) &&
                formValues.range?.start !== formValues.range?.target && (
                  <InfoPanel>
                    Progress will move from{' '}
                    <strong>{formValues.range?.start}</strong> to{' '}
                    <strong>{formValues.range?.target}</strong>. Progress is
                    calculated automatically as the value{' '}
                    {metricDirection(
                      Number(formValues.range?.start),
                      Number(formValues.range?.target),
                    ) === 'decrease'
                      ? 'decreases.'
                      : 'increases.'}
                  </InfoPanel>
                )}

              <FormSection>
                <Label>Updates</Label>
                <SegmentedControl role="group" aria-label="KPI updates">
                  {updateOptions.map((option) => (
                    <KpiSegmentedControlButton
                      key={option.value}
                      type="button"
                      active={selectedKpiUpdate === option.value}
                      aria-pressed={selectedKpiUpdate === option.value}
                      onClick={() => {
                        setSelectedKpiUpdate(option.value);
                        handleFormValueChange(
                          option.value,
                          'measurement.preference',
                        );
                      }}>
                      {option.label}
                    </KpiSegmentedControlButton>
                  ))}
                </SegmentedControl>
                <Helper
                  state={errors.measurement?.preference ? 'error' : 'default'}
                  children={errors.measurement?.preference?.message || ''}
                />
                <InfoPanel>
                  {selectedKpiUpdate === 'sum' ? (
                    <>
                      Update values are <strong>summed</strong>. Useful for
                      counting cumulative things like deals closed or calls
                      made.
                    </>
                  ) : selectedKpiUpdate === 'average' ? (
                    <>
                      Update values are <strong>averaged</strong>. Good for
                      rates and satisfaction scores that shouldn&apos;t
                      compound.
                    </>
                  ) : (
                    <>
                      Each update records a value. Progress uses the{' '}
                      <strong>most recent entry</strong> against your target.
                    </>
                  )}
                </InfoPanel>
              </FormSection>
            </KpiMetricFields>
          ) : (
            <ResultsSection>
              <Divider />
              <Controller
                control={control}
                name="measurement.unit"
                defaultValue={MeasurementType.BINARY}
                render={({value}) => (
                  <input
                    type="hidden"
                    value={value || MeasurementType.BINARY}
                    readOnly
                  />
                )}
              />
              <Controller
                control={control}
                name="keyResults"
                render={({value}) => (
                  <KeyResults
                    variant="createGoal"
                    compact={Boolean(editheaderShow)}
                    defaultValue={value || []}
                    options={members}
                    setValue={updateKeyResult}
                    errors={errors.keyResults}
                    helper={(errors as any).keyResults?.message || ''}
                    state={errors.keyResults ? 'error' : 'default'}
                  />
                )}
              />
            </ResultsSection>
          )}

          <FormActions $sidePanel={Boolean(editheaderShow)}>
            {!isEditing ? (
              <CreateGoalSubmitButton
                width="full"
                type="submit"
                data-form-action
                isLoading={isSubmitting}
                disabled={isSubmitting || !!exploreTemplate}
                aria-busy={isSubmitting}>
                Create goal
              </CreateGoalSubmitButton>
            ) : (
              <Button
                width="full"
                type="submit"
                style={{padding: '12px 24px'}}
                isLoading={Boolean(loader?.edit)}
                disabled={Boolean(loader?.edit) || isEditingDisabled}>
                Save changes
              </Button>
            )}
          </FormActions>
        </FormStack>
      </form>
    );
  },
);
